import { useEffect, useState } from "react";

import { fetchAISuggestions, type AISuggestions } from "@/lib/api";

type Field = "strengths" | "doses" | "frequency" | "administrationNotes";

/** Each request uses the existing audited medication LLM suggestion pipeline. */
export function useMedicationFieldSuggestions(
    visitId: string | undefined,
    productName: string,
    fields: Field[],
    enabled = true,
    strength?: string
) {
    const fieldsKey = fields.join(",");
    const requestKey = JSON.stringify([visitId, productName, fieldsKey, enabled, strength]);
    const [result, setResult] = useState<{
        key: string;
        suggestions: AISuggestions | null;
        loading: boolean;
    }>({ key: "", suggestions: null, loading: false });
    const canRequest = enabled && Boolean(visitId && productName.trim() && fieldsKey);
    useEffect(() => {
        if (!canRequest || !visitId) {
            return;
        }
        let cancelled = false;
        setResult({ key: requestKey, suggestions: null, loading: true });
        fetchAISuggestions(
            visitId,
            productName,
            undefined,
            fieldsKey.split(",") as Field[],
            true,
            strength
        )
            .then((suggestions) => {
                if (!cancelled) {
                    setResult({ key: requestKey, suggestions, loading: false });
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setResult({ key: requestKey, suggestions: null, loading: false });
                }
            });
        return () => {
            cancelled = true;
        };
    }, [requestKey, canRequest, visitId, productName, fieldsKey, strength]);
    // Never show a previous product's strengths while its replacement loads.
    return canRequest && result.key === requestKey
        ? result
        : { suggestions: null, loading: canRequest };
}
